import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { notification } from 'antd';
import moment from 'moment';
import { getExternalLoginStartUrl } from '~/common/utils/googleOauth';

import { Clock, LoadingSpinner } from '~/ui/assets/icons';
import useIsAuthenticated from '~/hooks/useIsAuthenticated';
import { remoteValues } from '~/common/utils/firebase/remoteConfig';

import { TimerContainer, Timer, NotificationButton } from './SessionTimer.styled';

export const isOAuthMethod = localStorage.getItem('authMethod') === 'google';

export function SessionTimer() {
  const { token, refreshToken } = useIsAuthenticated();
  const [timeLeft, setTimeLeft] = useState<string>('00:00');
  const [isCloseToExpire, setIsCloseToExpire] = useState<boolean>(false);
  const { allowRefreshSession } = remoteValues;
  const [sleepTime, setSleepTime] = useState<number>(0);

  const expirationAlertLimit = Number(
    localStorage.getItem('expirationAlertLimit') || remoteValues?.expirationAlertLimit,
  );

  const expiration = useMemo(() => {
    try {
      const parsedExp = JSON.parse(atob(token.split('.')[1]))?.exp;
      return parsedExp ? parsedExp * 1000 : null;
    } catch (e) {
      return null;
    }
  }, [token]);

  const handleRefreshSession = useCallback(() => {
    notification.destroy();
    notification.open({
      message: 'Renovando sessão...',
      top: 80,
      duration: 0,
      icon: <LoadingSpinner color="#D80073" />,
    });

    window.open(getExternalLoginStartUrl({ keepToken: true }), 'oAuthBlankWindow');
  }, []);

  const description = useMemo(
    () => (
      <>
        <p>Você será desconectado em breve. </p>
        {allowRefreshSession && isOAuthMethod && (
          <NotificationButton onClick={handleRefreshSession}>Renovar sessão</NotificationButton>
        )}
      </>
    ),
    [allowRefreshSession],
  );

  const afterReceiveMessage = (event) => {
    if (event.origin !== window.location.origin) return;

    if (event.data.type === 'OAUTH_DONE') {
      notification.destroy();
      localStorage.setItem('authenticationToken', event.data?.token);
      notification.success({ message: 'Sessão renovada com sucesso', top: 80 });
      refreshToken();
    }

    if (event.data.type === 'OAUTH_ERROR') {
      notification.destroy();
      notification.error({ message: 'Não foi possível renovar a sessão', top: 80 });
    }
  };

  useEffect(() => {
    if (isCloseToExpire) {
      notification.warning({
        message: 'Sua sessão está prestes a expirar',
        description,
        duration: 0,
        top: 80,
        onClose: () => {
          setIsCloseToExpire(false);
          setSleepTime(60);
        },
      });
    }

    return () => {
      notification.destroy();
    };
  }, [isCloseToExpire]);

  useEffect(() => {
    const futureDate = moment(expiration);

    const interval = setInterval(() => {
      const now = moment();
      const duration = moment.duration(futureDate.diff(now));
      const minutes = duration.minutes();
      const seconds = duration.seconds();

      const formattedTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

      if (duration.asMinutes() <= (expirationAlertLimit || 5) && sleepTime === 0) {
        setIsCloseToExpire(true);
      } else {
        setIsCloseToExpire(false);
      }

      if (duration.asSeconds() <= 0) {
        clearInterval(interval);
      } else {
        setTimeLeft(formattedTime);
      }

      if (sleepTime > 0) {
        setSleepTime(sleepTime - 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiration, sleepTime]);

  useEffect(() => {
    window.addEventListener('message', afterReceiveMessage);
    return () => {
      window.removeEventListener('message', afterReceiveMessage);
    };
  }, []);

  return (
    <TimerContainer>
      <Clock /> <Timer>{timeLeft}</Timer>
    </TimerContainer>
  );
}
